/**
 * PATH: packages/ui/src/kanban/components/lane/useLaneAiBulk.ts
 * FILE_ID: UI-KANBAN-LANE-AI-BULK-001
 * PURPOSE: Accept or reject every AI suggestion currently in this lane.
 * ROLE IN ARCHITECTURE: State hook extracted from useKanbanLane.
 * MAIN EXPORTS: useLaneAiBulk
 * NON-RESPONSIBILITIES: Does not render the triage bulk bar.
 */
import { useCallback } from 'react'

import { getCardAllocationId } from '../../allocation-source'
import type { KanbanApi } from '../../api'
import type { Card } from '../../types'

type Notify = (level: 'success' | 'error' | 'info', message: string) => void

async function runAllocationBulk(
  ids: string[],
  act: (id: string) => Promise<unknown>,
  label: { done: 'Accepted' | 'Rejected'; failed: 'accept' | 'reject' },
  notify: Notify | undefined,
  onDone: (() => void) | undefined,
) {
  let ok = 0
  let fail = 0
  await Promise.all(
    ids.map(async (id) => {
      try {
        await act(id)
        ok++
      } catch {
        fail++
      }
    }),
  )
  onDone?.()
  if (!notify) return
  const plural = (n: number) => `AI suggestion${n === 1 ? '' : 's'}`
  if (fail === 0) notify('success', `${label.done} ${ok} ${plural(ok)}`)
  else if (ok === 0) notify('error', `Failed to ${label.failed} ${fail} ${plural(fail)}`)
  else notify('info', `${label.done} ${ok}, failed ${fail}`)
}

function allocationIds(cards: Card[]): string[] {
  return cards.map((card) => getCardAllocationId(card)).filter((id): id is string => !!id)
}

export function useLaneAiBulk({
  api,
  notify,
  onAiAllocationChanged,
  aiCards,
  showUcAiTabs,
  triageTab,
}: {
  api?: KanbanApi
  notify?: Notify
  onAiAllocationChanged?: () => void
  aiCards: Card[]
  showUcAiTabs: boolean
  triageTab: string
}) {
  const bulkAcceptAi = useCallback(async () => {
    if (!api?.confirmAiAllocation) return
    const ids = allocationIds(aiCards)
    if (ids.length === 0) return
    await runAllocationBulk(
      ids,
      (id) => api.confirmAiAllocation!(id),
      { done: 'Accepted', failed: 'accept' },
      notify,
      onAiAllocationChanged,
    )
  }, [aiCards, api, notify, onAiAllocationChanged])

  const bulkRejectAi = useCallback(async () => {
    if (!api?.rejectAiAllocation) return
    const ids = allocationIds(aiCards)
    if (ids.length === 0) return
    await runAllocationBulk(
      ids,
      (id) => api.rejectAiAllocation!(id, { reason: 'bulk_reject' }),
      { done: 'Rejected', failed: 'reject' },
      notify,
      onAiAllocationChanged,
    )
  }, [aiCards, api, notify, onAiAllocationChanged])

  const showBulkBar =
    showUcAiTabs &&
    triageTab === 'ai' &&
    aiCards.length > 0 &&
    !!api?.confirmAiAllocation &&
    !!api?.rejectAiAllocation

  return { bulkAcceptAi, bulkRejectAi, showBulkBar }
}
